import { OrganizationDetailFragment, useResponseNotification } from '@alice-financial/alice-sdk'
import { Box, CircularProgress, HealthcheckItem, Typography } from '@alice-financial/pretext-ui'
import { useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FinchConnectReturnParam,
  useFinchIframe,
  useIsFinchOnboardingEnabledForPlatform,
} from '../../externalServices/finch/useFinchIframe'
import { useCurrentOrgRootPath, useOrg, useOrgQuery } from '../organization/useOrg'
import { FinchConnected } from './FinchConnected'
import { getFinchConnections } from './finchConnectionUtils'
import { FinchNotConnected } from './FinchNotConnected'
import { useFinchSetupStatus } from './useFinchSetupStatus'

/**
 * Finch Connection Flow:
 * 1. Frontend sends connection label to backend
 * 2. Backend requests session ID from Finch (using connection label as customer name + random ID as customer ID)
 * 3. Session ID returned to frontend and injected into Finch Connect iframe
 * 4. User authenticates through iframe
 * 5. Authorization code generated and exchanged for access token at backend
 */
export const EnabledFinchSetup = () => {
  const { data: orgData, isFetching } = useOrgQuery()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { notifyError } = useResponseNotification()

  // State to track when the Finch iframe is currently open
  const [isFinchIframeOpened, setIsFinchIframeOpened] = React.useState<boolean>(false)

  const updateFinchConnectParam = (value: FinchConnectReturnParam) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('finch_connect', value)
    setSearchParams(newSearchParams)
  }

  const onSuccessConnectingPayrollAccount = () => {
    // On success, invalidate the org query cache and signal step completion.
    setIsFinchIframeOpened(false) // Iframe is closed after success
    queryClient.invalidateQueries(useOrg.getKey())
    updateFinchConnectParam('completed')
  }

  const onErrorConnectingPayrollAccount = () => {
    setIsFinchIframeOpened(false)
    notifyError(
      'Unable to connect to payroll provider. Please try again or contact support if the issue persists.'
    )
    updateFinchConnectParam('error_occurred')
  }

  const onCloseConnectingPayrollAccount = () => {
    setIsFinchIframeOpened(false)
    queryClient.invalidateQueries(useOrg.getKey())
    updateFinchConnectParam('iframe_closed')
  }

  const { openFinchIframe, isTokenExchangeLoading } = useFinchIframe({
    onSuccessConnectingPayrollAccount,
    onErrorConnectingPayrollAccount,
    onCloseConnectingPayrollAccount,
  })

  const finchConnections = getFinchConnections(orgData?.organization)

  const { id: organizationId, name: organizationName } = orgData?.organization || {}

  if (!openFinchIframe) {
    return <Typography>Failed to initialize step, please move to the next step.</Typography>
  }

  if (isFetching || isTokenExchangeLoading) {
    return (
      <Box textAlign="center" py={2}>
        <CircularProgress />
      </Box>
    )
  }

  // If somehow no org data is available, throw an error
  if (!organizationId || !organizationName) {
    throw new Error('EnabledFinchSetup: organizationId or organizationName is required')
  }

  if (finchConnections.connected.length > 0) {
    return (
      <FinchConnected
        organizationId={organizationId}
        doneFinchConnections={finchConnections.connected}
        needingAuthFinchConnections={finchConnections.needAuth}
        allFinchConnections={finchConnections.all}
        isFinchIframeOpened={isFinchIframeOpened}
        onFinchSessionCreated={(finchSessionInfo) => {
          setIsFinchIframeOpened(true)
          openFinchIframe(finchSessionInfo.finchSessionId, finchSessionInfo.finchConnectionId)
        }}
      />
    )
  }

  return (
    <FinchNotConnected
      organizationName={organizationName}
      organizationId={organizationId}
      needingAuthFinchConnections={finchConnections.needAuth}
      allFinchConnections={finchConnections.all}
      isFinchIframeOpened={isFinchIframeOpened}
      onFinchSessionCreated={(finchSessionInfo) => {
        setIsFinchIframeOpened(true)
        openFinchIframe(finchSessionInfo.finchSessionId, finchSessionInfo.finchConnectionId)
      }}
    />
  )
}

export const FinchSetupHealthcheck = ({ org, todo }: { org: OrganizationDetailFragment; todo?: boolean }) => {
  const currentOrgRootPath = useCurrentOrgRootPath()
  const statusResult = useFinchSetupStatus(org)
  const finchConnectEnabled = useIsFinchOnboardingEnabledForPlatform(org)

  if (!statusResult || !finchConnectEnabled) return null

  return (
    <HealthcheckItem
      to={`${currentOrgRootPath}/payroll/read-access`}
      status={statusResult.status}
      primary="Read access to employee roster"
      secondary={statusResult.secondary}
      todo={todo}
    >
      <EnabledFinchSetup />
    </HealthcheckItem>
  )
}
